import { useState, useEffect, ChangeEvent } from 'react';
import { Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, InputAdornment, Box, IconButton, Collapse, FormControl, Autocomplete, Chip, CircularProgress, Stack, Tooltip, useMediaQuery } from '@mui/material';
import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { getServiceTypes, getPlans, createPlan, updatePlan, deletePlan, getProductBrands } from '@services/planService';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { translateTextStream } from '@services/aiTranslateService';
import { PageContainer, PageHeader, SectionCard, EmptyState, ResponsiveTable } from '../components/ui';
import type { Column } from '../components/ui';

type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';

interface SubServiceType {
  _id: string;
  name: string;
  zh_name: string;
}

interface ServiceType {
  _id: string;
  name: string;
  subServiceTypes?: SubServiceType[];
  [key: string]: unknown;
}

interface ProductBrand {
  _id: string;
  name: string;
  zh_name: string;
  description?: string;
}

interface PlanItem {
  _id: string;
  name: string;
  zh_name: string;
  description?: string;
  zh_description?: string;
  serviceTypeId: string;
  subServiceTypeId?: string;
  price: number | string;
  duration: number | string;
  featuredImage?: string;
  createdBy?: {
    displayName?: string;
  };
  [key: string]: unknown;
}

interface PlanFormValues {
  _id?: string;
  name: string;
  zh_name: string;
  description: string;
  zh_description: string;
  serviceTypeId: string;
  subServiceTypeId: string;
  otherServiceType: string;
  price: string;
  currency: string;
  duration: string;
  featuredImage: string;
  files: File[];
  productBrands: ProductBrand[];
  [key: string]: unknown;
}

interface PlanFilters {
  serviceTypeId: string;
  subServiceTypeId: string;
  priceRange: string;
}

const PlanPageNew: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [productBrands, setProductBrands] = useState<ProductBrand[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<PlanFilters>({
    serviceTypeId: '',
    subServiceTypeId: '',
    priceRange: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);

  const [isTranslatingEnToZhName, setIsTranslatingEnToZhName] = useState<boolean>(false);
  const [isTranslatingZhToEnName, setIsTranslatingZhToEnName] = useState<boolean>(false);
  const [isTranslatingEnToZhDesc, setIsTranslatingEnToZhDesc] = useState<boolean>(false);
  const [isTranslatingZhToEnDesc, setIsTranslatingZhToEnDesc] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: {
      errors
    }
  } = useForm<PlanFormValues>({
    defaultValues: {
      name: '',
      zh_name: '',
      description: '',
      zh_description: '',
      serviceTypeId: '',
      subServiceTypeId: '',
      otherServiceType: '',
      price: '',
      currency: '',
      duration: '',
      featuredImage: '',
      files: [],
      productBrands: []
    },
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const files = watch('files') || [];
  const featuredImage = watch('featuredImage');

  const watchedName = watch('name');
  const watchedZhName = watch('zh_name');
  const watchedDescription = watch('description');
  const watchedZhDescription = watch('zh_description');

  const selectedServiceTypeId = useWatch({
    control,
    name: "serviceTypeId",
  });

  const fetchData = async () => {
    try {
      const [serviceTypesResult, plansResult, productBrandsResult] = await Promise.all([
        getServiceTypes(),
        getPlans(),
        getProductBrands()
      ]);
      setServiceTypes(Array.isArray(serviceTypesResult) ? serviceTypesResult : []);
      // Backend returns { plans: [...] }, extract the array
      const plansData = plansResult.data ?? null;
      const plansArray = (plansData as { plans?: PlanItem[] } | null)?.plans || plansData || [];
      setPlans(Array.isArray(plansArray) ? plansArray as PlanItem[] : []);
      setProductBrands(Array.isArray(productBrandsResult) ? productBrandsResult : []);
    } catch (error) {
      toast.error(t('common.errorOccurred'));
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: PlanFormValues) => {
    try {
      if (data._id) {
        await updatePlan(data as Record<string, unknown> & { _id: string });
        toast.success(t('common.updateSuccess'));
      } else {
        await createPlan(data as Record<string, unknown>);
        toast.success(t('common.createSuccess'));
      }
      await fetchData();
      reset();
      setShowModal(false);
    } catch (error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || t('common.errorOccurred'));
      console.error(error);
    }
  };

  const handleEdit = (plan: PlanItem) => {
    Object.keys(plan).forEach((key: string) => {
      (setValue as (name: string, value: unknown) => void)(key, plan[key] || '');
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await deletePlan(planToDelete!);
      toast.success(t('common.deleteSuccess'));
      await fetchData();
    } catch (error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || t('common.errorOccurred'));
      console.error(error);
    } finally {
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  const confirmDelete = (id: string) => {
    setPlanToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCreate = () => {
    reset();
    setShowModal(true);
  };

  const handleFeaturedImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setValue('files', newFiles);
  };

  const handleStreamTranslate = async (
    sourceText: string,
    sourceLang: LanguageCode,
    targetLang: LanguageCode,
    targetFieldName: keyof PlanFormValues,
    setLoadingState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!sourceText) {
      toast.info(t('common.sourceFieldEmpty'));
      return;
    }
    setLoadingState(true);

    const formCompatibleSetTargetState = (valueOrUpdater: string | ((prev: string) => string)) => {
      const fieldName = targetFieldName as string;
      const currentFieldValue = (getValues as (name: string) => unknown)(fieldName) as string || '';
      const setValueFn = setValue as (name: string, value: unknown, options?: Record<string, boolean>) => void;
      if (typeof valueOrUpdater === 'function') {
        setValueFn(fieldName, valueOrUpdater(currentFieldValue), { shouldDirty: true, shouldTouch: true });
      } else {
        setValueFn(fieldName, valueOrUpdater, { shouldDirty: true, shouldTouch: true });
      }
    };

    try {
      await translateTextStream(
        sourceText,
        targetLang,
        sourceLang,
        formCompatibleSetTargetState,
        () => {
          setLoadingState(false);
          toast.success(t('common.translationSuccess'));
        },
        (error: Error) => {
          setLoadingState(false);
          toast.error(error.message || t('common.translationError'));
        }
      );
    } catch (error) {
      setLoadingState(false);
      const errorMessage = error instanceof Error ? error.message : t('common.translationError');
      toast.error(errorMessage);
      console.error('Error initiating translation stream:', error);
    }
  };

  const isAnyTranslationLoading = isTranslatingEnToZhName || isTranslatingZhToEnName || isTranslatingEnToZhDesc || isTranslatingZhToEnDesc;

  const filteredPlans = (Array.isArray(plans) ? plans : []).filter(plan => {
    const matchesSearch =
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.zh_name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesServiceType = !filters.serviceTypeId || plan.serviceTypeId === filters.serviceTypeId;
    const matchesSubServiceType = !filters.subServiceTypeId || plan.subServiceTypeId === filters.subServiceTypeId;

    return matchesSearch && matchesServiceType && matchesSubServiceType;
  });

  const serviceTypeName = (id?: string) => serviceTypes.find((type) => type._id === id)?.name || '—';

  // --- Restyle helpers: presentation only ---
  const editButton = (plan: PlanItem) => (
    <Button
      size="small"
      color="primary"
      variant="outlined"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(plan)}
    >
      {t('planPage.edit')}
    </Button>
  );

  const deleteButton = (plan: PlanItem) => (
    <Button
      size="small"
      color="error"
      variant="outlined"
      startIcon={<DeleteOutlineIcon fontSize="small" />}
      onClick={() => confirmDelete(plan._id)}
    >
      {t('planPage.delete')}
    </Button>
  );

  const columns: Column<PlanItem>[] = [
    {
      key: 'name',
      header: t('planPage.name'),
      primary: true,
      render: (plan) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{plan.name}</Typography>
          {plan.zh_name && (
            <Typography variant="caption" color="text.secondary">{plan.zh_name}</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'price',
      header: t('planPage.price'),
      align: 'right',
      render: (plan) => <Typography variant="body2">HKD {plan.price}</Typography>,
    },
    {
      key: 'duration',
      header: t('planPage.duration'),
      align: 'right',
      render: (plan) => (
        <Typography variant="body2">
          {plan.duration} {t('planPageNew.minutesShort', 'min')}
        </Typography>
      ),
    },
    {
      key: 'serviceType',
      header: t('planPage.serviceType'),
      render: (plan) => (
        <Chip size="small" variant="outlined" label={serviceTypeName(plan.serviceTypeId)} />
      ),
    },
    {
      key: 'createdBy',
      header: t('planPage.createdBy'),
      render: (plan) => (
        <Typography variant="body2">{plan?.createdBy?.displayName || t('common.business')}</Typography>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      hideOnMobile: true,
      render: (plan) => (
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {editButton(plan)}
          {deleteButton(plan)}
        </Stack>
      ),
    },
  ];

  const createButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
      {t('planPage.createPlan')}
    </Button>
  );

  const translateButton = (opts: {
    onClick: () => void;
    loading: boolean;
    disabledReason?: string;
    label: string;
    disabled: boolean;
  }) => {
    const btn = (
      <span>
        <Button
          onClick={opts.onClick}
          disabled={opts.disabled}
          variant="outlined"
          size="small"
          startIcon={opts.loading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {opts.label}
        </Button>
      </span>
    );
    if (opts.disabled && opts.disabledReason) {
      return <Tooltip title={opts.disabledReason}>{btn}</Tooltip>;
    }
    return btn;
  };

  return (
    <PageContainer>
      <PageHeader
        title={t('planPage.existingPlans')}
        subtitle={t('planPageNew.subtitle', 'Manage the service plans your salon offers.')}
        icon={<LocalOfferIcon />}
        action={createButton}
        classicPath="/classic/plans"
      />

      <SectionCard title={t('planPageNew.searchAndFilter', 'Search & filter')} sx={{ mb: 2.5 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowFilters(!showFilters)}>
                    <FilterListIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Collapse in={showFilters}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <TextField
                    select
                    fullWidth
                    label={t('planPage.serviceType')}
                    value={filters.serviceTypeId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, serviceTypeId: e.target.value })}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {serviceTypes.map(type => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </SectionCard>

      <ResponsiveTable<PlanItem>
        columns={columns}
        rows={filteredPlans}
        getRowKey={(plan) => plan._id}
        renderMobileActions={(plan) => (
          <>
            {editButton(plan)}
            {deleteButton(plan)}
          </>
        )}
        emptyState={
          <EmptyState
            icon={<LocalOfferIcon />}
            title={t('planPageNew.noPlans', 'No plans yet')}
            description={t('planPageNew.noPlansDescription', 'Create your first service plan to start listing what your salon offers.')}
            action={createButton}
          />
        }
      />

      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {getValues('_id') ? t('planPage.editPlan') : t('planPage.createPlanTitle')}
        </DialogTitle>
        <DialogContent dividers>
          {isAnyTranslationLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          <form id="plan-form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              {translateButton({
                onClick: () => handleStreamTranslate(watchedName, 'en', 'zh-HK', 'zh_name', setIsTranslatingEnToZhName),
                loading: isTranslatingEnToZhName,
                disabled: isAnyTranslationLoading || !watchedName,
                disabledReason: !watchedName ? t('planPageNew.enterEnglishFirst', 'Enter the English name first') : undefined,
                label: `${t('quickListingWizard.translateWithAI')} ${t('common.toChinese')}`,
              })}
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{ required: t('common.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('planPage.name')}
                  fullWidth
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : ''}
                  disabled={isAnyTranslationLoading}
                  InputLabelProps={{ shrink: !!field.value || !!watchedName }}
                />
              )}
            />

            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              {translateButton({
                onClick: () => handleStreamTranslate(watchedZhName, 'zh-HK', 'en', 'name', setIsTranslatingZhToEnName),
                loading: isTranslatingZhToEnName,
                disabled: isAnyTranslationLoading || !watchedZhName,
                disabledReason: !watchedZhName ? t('planPageNew.enterChineseFirst', 'Enter the Chinese name first') : undefined,
                label: `${t('quickListingWizard.translateWithAI')} ${t('common.toEnglish')}`,
              })}
            </Box>
            <Controller
              name="zh_name"
              control={control}
              rules={{ required: t('common.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('planPage.chineseName')}
                  fullWidth
                  margin="normal"
                  error={!!errors.zh_name}
                  helperText={errors.zh_name ? errors.zh_name.message : ''}
                  disabled={isAnyTranslationLoading}
                  InputLabelProps={{ shrink: !!field.value || !!watchedZhName }}
                />
              )}
            />

            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              {translateButton({
                onClick: () => handleStreamTranslate(watchedDescription, 'en', 'zh-HK', 'zh_description', setIsTranslatingEnToZhDesc),
                loading: isTranslatingEnToZhDesc,
                disabled: isAnyTranslationLoading || !watchedDescription,
                disabledReason: !watchedDescription ? t('planPageNew.enterEnglishDescFirst', 'Enter the English description first') : undefined,
                label: `${t('quickListingWizard.translateWithAI')} ${t('common.toChinese')}`,
              })}
            </Box>
            <Controller
              name="description"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t('planPage.description')}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  error={!!error}
                  helperText={error ? t('common.required') : ''}
                  disabled={isAnyTranslationLoading}
                  InputLabelProps={{ shrink: !!field.value || !!watchedDescription }}
                />
              )}
            />

            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              {translateButton({
                onClick: () => handleStreamTranslate(watchedZhDescription, 'zh-HK', 'en', 'description', setIsTranslatingZhToEnDesc),
                loading: isTranslatingZhToEnDesc,
                disabled: isAnyTranslationLoading || !watchedZhDescription,
                disabledReason: !watchedZhDescription ? t('planPageNew.enterChineseDescFirst', 'Enter the Chinese description first') : undefined,
                label: `${t('quickListingWizard.translateWithAI')} ${t('common.toEnglish')}`,
              })}
            </Box>
            <Controller
              name="zh_description"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t('planPage.chineseDescription')}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  error={!!error}
                  helperText={error ? t('common.required') : ''}
                  disabled={isAnyTranslationLoading}
                  InputLabelProps={{ shrink: !!field.value || !!watchedZhDescription }}
                />
              )}
            />

            <Controller
              name="serviceTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    {...field}
                    select
                    label={t('planPage.serviceType')}
                    fullWidth
                    margin="normal"
                    error={!!error}
                    helperText={error ? t('common.required') : ''}
                    disabled={isAnyTranslationLoading}
                  >
                    <MenuItem value="">{t('planPage.selectServiceType')}</MenuItem>
                    {serviceTypes.map(type => (
                      <MenuItem key={type._id} value={type._id}>{type.name}</MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />

            <Controller
              name="subServiceTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => {
                const selectedType = serviceTypes.find(type => type._id === selectedServiceTypeId);

                return (
                  <TextField
                    {...field}
                    select
                    label={t('planPage.subServiceType')}
                    fullWidth
                    margin="normal"
                    error={!!error}
                    helperText={error ? t('common.required') : ''}
                    disabled={!selectedServiceTypeId || isAnyTranslationLoading}
                  >
                    <MenuItem value="">{t('planPage.selectSubServiceType')}</MenuItem>
                    {selectedType?.subServiceTypes?.map(subType => (
                      <MenuItem key={subType._id} value={subType._id}>
                        {subType.name} - {subType.zh_name}
                      </MenuItem>
                    )) || []}
                  </TextField>
                );
              }}
            />

            <Controller
              name="price"
              control={control}
              rules={{
                required: t('common.required'),
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message: t('common.invalidPrice')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('planPage.price')}
                  fullWidth
                  margin="normal"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : t('planPageNew.priceHelper', 'e.g. 388')}
                  disabled={isAnyTranslationLoading}
                />
              )}
            />

            <Controller
              name="duration"
              control={control}
              rules={{
                required: t('common.required'),
                validate: (value: string) => Number(value) % 15 === 0 || t('common.invalidDuration')
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t('planPage.duration')}
                  fullWidth
                  margin="normal"
                  error={!!error}
                  helperText={error ? error.message : t('planPageNew.durationHelper', 'in minutes, multiples of 15')}
                  disabled={isAnyTranslationLoading}
                />
              )}
            />

            <Controller
              name="productBrands"
              control={control}
              render={({ field }) => (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Autocomplete
                      multiple
                      options={productBrands}
                      getOptionLabel={(option) => `${option.name} (${option.zh_name})`}
                      value={field.value || []}
                      onChange={(_: unknown, newValue: ProductBrand[]) => {
                        field.onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t('planPage.productBrand')} (${t('common.optional')})`}
                          fullWidth
                          disabled={isAnyTranslationLoading}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps}>
                            <Box>
                              <Typography variant="body1">
                                {option.name} ({option.zh_name})
                              </Typography>
                              {option.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderTags={(value: ProductBrand[], getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...chipProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              {...chipProps}
                              label={`${option.name} (${option.zh_name})`}
                            />
                          );
                        })
                      }
                      isOptionEqualToValue={(option: ProductBrand, value: ProductBrand) => option._id === value._id}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Box>
              )}
            />

            <Controller
              name="featuredImage"
              control={control}
              render={({ field: _field, fieldState: { error } }) => (
                <>
                  <TextField
                    type="file"
                    onChange={handleFeaturedImageSelect}
                    label={t('planPage.featuredImage')}
                    fullWidth
                    margin="normal"
                    error={!!error}
                    helperText={error ? t('common.required') : ''}
                    disabled={isAnyTranslationLoading}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                  {files.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      {files[0] && (
                        <img src={URL.createObjectURL(files[0])} alt="Featured Image Preview" style={{ maxWidth: '100%', height: 'auto', maxHeight: '160px' }} />
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      {!!featuredImage && (
                        <img src={`${import.meta.env.VITE_R2_SALON_ENDPOINT}/${featuredImage}`} alt="Featured Image Preview" style={{ maxWidth: '100%', height: 'auto', maxHeight: '160px' }} />
                      )}
                    </Box>
                  )}
                </>
              )}
            />
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShowModal(false)} color="inherit">
            {t('commonConfirmationDialog.cancel')}
          </Button>
          <Button type="submit" form="plan-form" variant="contained">
            {getValues('_id') ? t('planPage.updatePlan') : t('planPage.createPlanButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('commonConfirmationDialog.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('commonConfirmationDialog.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('commonConfirmationDialog.cancel')}</Button>
          <Button color="error" onClick={handleDelete}>{t('commonConfirmationDialog.confirm')}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default PlanPageNew;
